import React, { useEffect, useRef, useState } from 'react'

import { Pagination } from '@/core/api/dtos/common/metaPagination'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useAuth } from '@/core/hooks/useAuth'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import Icon from '@/shared/components/atoms/Icon'
import { Input } from '@/shared/components/atoms/Input'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'
import { cn } from '@/shared/utils/helpers'

import PlaylistCard from './components/PlaylistCard'
import PlaylistSkeleton from './components/PlaylistSkeleton'
import ProfileIncompleteBanner from './components/ProfileIncompleteBanner'
import RequestCustomPlaylistModal from './components/RequestCustomPlaylistModal'

// Types
interface ViewAllPlaylistsPageUIProps {
	title: string
	searchQuery: string
	onSearchChange: (query: string) => void
	playlists: Playlist[]
	isLoading: boolean
	showSearchResults: boolean
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	loadingPlaylistId?: number | null
	onRequestCustomPlaylistClick?: () => void
	isRequestModalOpen?: boolean
	onCloseRequestModal?: () => void
	profileIncomplete?: boolean
	hasMore: boolean
	isLoadingMore: boolean
	onLoadMore: () => void
	playlistType: 'assigned' | 'favorites' | 'all' | 'in-use'
	pagination?: Pagination
	currentPage: number
	favoritePlaylistIds?: Set<number>
}

// Main Component
const ViewAllPlaylistsPageUI: React.FC<ViewAllPlaylistsPageUIProps> = ({
	title,
	searchQuery,
	onSearchChange,
	playlists,
	isLoading,
	showSearchResults,
	onFavoriteClick,
	loadingPlaylistId,
	onRequestCustomPlaylistClick,
	isRequestModalOpen = false,
	onCloseRequestModal,
	profileIncomplete,
	hasMore,
	isLoadingMore,
	onLoadMore,
	playlistType,
	pagination,
	currentPage,
	favoritePlaylistIds,
}) => {
	const { t } = useTranslation('common')
	const observerTarget = useRef<HTMLDivElement>(null)

	const [isProfileBannerOpen, setIsProfileBannerOpen] = useState(false)

	const { user: currentUser } = useAuth()

	const canRequestCustomPlaylist =
		(currentUser?.account?.subscriptions?.filter(
			(subscription) => subscription.plan?.metadata?.canRequestPlaylists
		).length || 0) > 0

	const handleRequestClick = () => {
		if (profileIncomplete) {
			// Show ProfileIncompleteBanner modal instead of RequestCustomPlaylistModal
			setIsProfileBannerOpen(true)
		} else {
			// Show RequestCustomPlaylistModal if profile is complete
			if (onRequestCustomPlaylistClick) {
				onRequestCustomPlaylistClick()
			}
		}
	}

	const isFavoritesCarousel = playlistType === 'favorites'

	// Infinite scroll observer
	useEffect(() => {
		if (showSearchResults) {
			return // Don't use infinite scroll for search
		}

		// Calculate hasMore from pagination
		const calculatedHasMore = pagination
			? currentPage < pagination.total_pages
			: false

		if (!calculatedHasMore) {
			return // Don't observe if no more pages
		}

		// Wait a bit for the DOM to update and ref to be attached
		let observer: IntersectionObserver | null = null
		let currentTarget: HTMLDivElement | null = null

		const timeoutId = setTimeout(() => {
			observer = new IntersectionObserver(
				(entries) => {
					const entry = entries[0]

					if (
						entry.isIntersecting &&
						calculatedHasMore &&
						!isLoadingMore &&
						!isLoading
					) {
						onLoadMore()
					}
				},
				{
					threshold: 0,
					rootMargin: '200px', // Trigger 200px before reaching the bottom
				}
			)

			currentTarget = observerTarget.current
			if (currentTarget) {
				observer.observe(currentTarget)
			}
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			if (observer && currentTarget) {
				observer.unobserve(currentTarget)
			}
		}
	}, [
		hasMore,
		isLoadingMore,
		isLoading,
		onLoadMore,
		showSearchResults,
		pagination,
		currentPage,
	])

	return (
		<PageLayout title={title} backButton={{ show: true, to: '/music' }}>
			<div className="space-y-6">
				<div className="flex flex-col lg:flex-row w-full items-stretch lg:items-center gap-4 justify-between">
					<div className="relative flex flex-1 lg:max-w-[400px]">
						<Icon
							name="search"
							width="16px"
							height="16px"
							className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-[1]"
						/>
						<Input
							type="search"
							placeholder={t('actions.search')}
							value={searchQuery}
							onChange={(e) => onSearchChange(e.target.value)}
							className="pl-[calc(16px+16px+8px)] w-full text-base"
						/>
					</div>
					<Button
						variant="default"
						className="rounded-full w-full lg:w-auto"
						onClick={handleRequestClick}
						disabled={!canRequestCustomPlaylist}
					>
						{t('entities.music.request_custom_playlist')}
						<Icon name="music-note-beamed" width="16px" height="16px" />
					</Button>
				</div>

				{/* Playlist Grid */}
				{isLoading && playlists.length === 0 ? (
					<div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 mb-8 max-md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
						{Array.from({ length: 25 }).map((_, index) => (
							<PlaylistSkeleton key={index} count={1} />
						))}
					</div>
				) : playlists.length > 0 ? (
					<>
						<div className="grid grid-cols-4 lg:grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 mb-8 max-md:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
							{playlists.map((playlist) => {
								const isFavorite = favoritePlaylistIds
									? favoritePlaylistIds.has(playlist.id)
									: isFavoritesCarousel
								return (
									<PlaylistCard
										key={playlist.id}
										playlist={playlist}
										onFavoriteClick={onFavoriteClick}
										showLocationIcon={
											playlistType === 'all' || playlistType === 'favorites'
										}
										showFavoriteIcon={true}
										isLoadingFavorite={loadingPlaylistId === playlist.id}
										isFavoritesCarousel={isFavoritesCarousel}
										isFavorite={isFavorite}
									/>
								)
							})}
						</div>
						{/* Infinite scroll trigger - always render when hasMore to enable observer */}
						{!showSearchResults &&
						pagination &&
						currentPage < pagination.total_pages ? (
							<div
								ref={observerTarget}
								className="min-h-[200px] mt-6 w-full flex flex-col"
							>
								{/* Always render a spacer for the observer to detect */}
								<div style={{ minHeight: '100px', width: '100%' }} />
							</div>
						) : null}
						{/* Floating loading indicator */}
						{isLoadingMore && (
							<div
								className={cn(
									'fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000]',
									'bg-card border border-border rounded-lg',
									'px-4 py-2 shadow-[0_4px_12px_rgba(0,0,0,0.15)]',
									'flex items-center justify-center backdrop-blur-[10px]',
									'animate-in slide-in-from-bottom-5 fade-in duration-300'
								)}
							>
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 border-2 border-[rgba(var(--foreground),0.3)] border-t-foreground rounded-full animate-spin" />
									<span className="text-foreground text-sm font-medium">
										{t('actions.loading')}
									</span>
								</div>
							</div>
						)}
					</>
				) : (
					<div className="flex items-center justify-center p-8">
						<p className="text-muted-foreground text-base m-0">
							{t('entities.music.no_playlists')}
						</p>
					</div>
				)}
			</div>
			<ProfileIncompleteBanner
				isOpen={isProfileBannerOpen}
				onClose={() => setIsProfileBannerOpen(false)}
			/>
			<RequestCustomPlaylistModal
				isOpen={isRequestModalOpen}
				onClose={onCloseRequestModal || (() => {})}
			/>
		</PageLayout>
	)
}

export default ViewAllPlaylistsPageUI
